import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * A dialog over the page.
 *
 * Used instead of expanding a row in place: an inline expansion is
 * fine for four rows and unusable at four hundred, because it moves
 * everything below it and you lose your position in the list.
 */
export const Modal = ({
  title, subtitle, onClose, onPrint, children,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  /** Shown only when the content is worth putting on paper. */
  onPrint?: () => void;
  children: ReactNode;
}) => {
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [onClose]);

  // Rendered outside the app, not inside it. Printing hides the
  // application shell, and a dialog nested within it would be
  // hidden along with everything else -- which is exactly what
  // produced a blank page.
  return createPortal(
    <div
      className="scrim"
      onClick={(e) => {
        // Only the backdrop closes. A click that starts inside and
        // ends outside, which is what dragging to select text does,
        // must not throw the dialog away.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal" role="dialog" aria-modal="true">
        <header className="modalHead">
          <div className="modalTitle">
            <h2>{title}</h2>
            {subtitle && <p className="sub">{subtitle}</p>}
          </div>
          {onPrint && <button onClick={onPrint}>Print</button>}
          <button className="iconBtn" onClick={onClose}
                  aria-label="Close">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
                 stroke="currentColor" strokeWidth="1.8"
                 strokeLinecap="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </header>
        <div className="modalBody">{children}</div>
      </div>
    </div>,
    document.body,
  );
};
